import React, { useState, useEffect } from 'react'
import { ChecklistItemWithResourceInfo, User } from 'TypeDefs/Goal'
import moment from 'moment'
import { useAppState } from 'Context/AppContext/AppContext'
import { Goal } from 'Generated/graphql-types'
import { TaskStatus } from './TaskStatus'
import { TaskTitle } from './TaskTitle'
import { TaskDueDate } from './TaskDueDate'
import { TaskAssignment } from './TaskAssignment'
import { TaskActions } from './TaskActions'
import { TaskLinks } from './TaskLinks'
import { taskAnalytics } from '../analytics.utils'
import {
  TaskActionsContainer,
  TaskInfoContainer,
  TaskContentContainer,
  TaskTitleContainer,
  DueDateContainer,
} from './TaskItem.styled'
import { TableRow } from '../TaskListV2/TaskListV2.styled'
import { useAssigneeEmail } from '../utils'

type TaskItemV2Props = {
  isResourceLoading: boolean
  onDuplicateTask: (task: ChecklistItemWithResourceInfo) => void
  onEditTask: (task: ChecklistItemWithResourceInfo) => void
  onUpsertTask: (task: Partial<ChecklistItemWithResourceInfo>) => Promise<void>
  task: ChecklistItemWithResourceInfo
  goal: Goal
}

export const TaskItemV2 = ({
  isResourceLoading,
  onDuplicateTask,
  onEditTask,
  onUpsertTask,
  task,
  goal,
}: TaskItemV2Props) => {
  const {
    nonprofitOrgConfig: {
      platformPlan,
      orgId: nonprofitOrgId,
      funderOrgId,
      role: userRole,
    },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()
  const [assignee, setAssignee] = useState<User | null>(task.owner ?? null)
  const [isHovered, setIsHovered] = useState(false)

  const baseEventProps = {
    assignerEmail: userEmail,
    assignerId: userId,
    funderOrgId,
    goalId: task.goalId,
    goalType: goal.type,
    isGoalSharedWithFunder: !!goal.FunderOrgToSharedGoal,
    nonprofitOrgId,
    platformPlan,
    templateLibraryId: goal.templateLibraryId || null,
    userId,
    userRole,
  }

  const getAssigneeEmail = useAssigneeEmail()

  useEffect(() => {
    setAssignee(task.owner ?? null)
  }, [task.owner])

  const handleStatusToggle = async () => {
    const isCompleting = !task.isCompleted
    await onUpsertTask({ ...task, isCompleted: isCompleting })

    if (isCompleting) {
      taskAnalytics.dispatchTaskCompleted(task, {
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner || null),
      })
    }
  }

  const handleDueDateChange = async (newDate: moment.Moment | null) => {
    const dueDate = newDate ? newDate.format('YYYY-MM-DD') : null
    await onUpsertTask({ ...task, dueDate })

    if (dueDate) {
      taskAnalytics.dispatchTaskDueDateSubmitted(task, dueDate, baseEventProps)
    } else {
      taskAnalytics.dispatchTaskDueDateDeleted(task, baseEventProps)
    }
  }

  const handleAssignOwner = async (newAssignee: User | null) => {
    setAssignee(newAssignee)
    await onUpsertTask({ ...task, owner: newAssignee })

    taskAnalytics.dispatchTaskAssigned(task, newAssignee, {
      ...baseEventProps,
      assigneeUserEmail: getAssigneeEmail(newAssignee),
    })

    if (newAssignee && newAssignee.id !== userId) {
      taskAnalytics.dispatchTaskAssignedNotificationSent(task, baseEventProps)
    }
  }

  const handleAcademyLinkClicked = () => {
    taskAnalytics.dispatchAcademyResourceClicked(task, baseEventProps)
  }

  const hasGroupId =
    task.checklistItemGroupId !== null &&
    task.checklistItemGroupId !== undefined

  const hasLinks = task.resource || task.linkUrl

  return (
    <TableRow
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(false)}
    >
      <TaskContentContainer>
        <TaskTitleContainer>
          <TaskStatus
            isCompleted={task.isCompleted ?? false}
            onStatusChange={handleStatusToggle}
          />
          <TaskTitle
            title={task.title}
            isCompleted={task.isCompleted ? 'true' : 'false'}
          />
        </TaskTitleContainer>
        {hasLinks && (
          <TaskInfoContainer>
            <TaskLinks
              resource={task.resource}
              linkUrl={task.linkUrl ?? ''}
              linkText={task.linkText ?? ''}
              isCompleted={task.isCompleted ?? false}
              isResourceLoading={isResourceLoading}
              onAcademyLinkClick={handleAcademyLinkClicked}
            />
          </TaskInfoContainer>
        )}
      </TaskContentContainer>
      <DueDateContainer>
        <TaskDueDate
          dueDate={task.dueDate ?? null}
          onDueDateChange={handleDueDateChange}
          hasGroupId={hasGroupId}
          isParentHovered={isHovered}
        />
      </DueDateContainer>
      <TaskActionsContainer>
        <TaskAssignment owner={assignee} onAssignOwner={handleAssignOwner} />
        <TaskActions
          goal={goal}
          onDuplicateTask={() => onDuplicateTask(task)}
          onEditTask={() => onEditTask(task)}
          task={task}
        />
      </TaskActionsContainer>
    </TableRow>
  )
}
